import { Client, contractAddress } from "@verified-network/verified-sdk";
import CLIENTABI from "../../utils/abis/Client.json";
import { ethers } from "ethers";
import { fetchUsersByAddresses } from "../subraphs/walletSubraph";
import { ChainConfig } from "../../utils/config";
import { handleCustodyTransaction } from "../../utils/helpers/hextrust";

/**
 * Note: This is not recommended because contract abi may not be up to date
 * @param {number} chainId
 * @param {Web3} web3
 * @returns web3's version of client contract to make any function call that is not available on Verified Sdk e.t.c
 */
export const getClientContractWeb3 = (chainId, web3) => {
  const clientAddress = contractAddress[chainId]
    ? contractAddress[chainId].Client
    : null;
  if (clientAddress) {
    return new web3.eth.Contract(CLIENTABI, clientAddress);
  } else {
    console.error("Client contract does not exist for chainId: ", chainId);
    return null;
  }
};

/**
 * Note: This is recommended because contract abi is always up to date with sdk version.
 * @param {number} chainId
 * @param {JsonRpcSigner} signer
 * @returns Verified Sdk version of client contract
 */
export const getClientContract = (chainId, signer) => {
  const clientAddress = contractAddress[chainId]
    ? contractAddress[chainId].Client
    : null;
  if (clientAddress) {
    return new Client(signer, clientAddress);
  } else {
    console.error("Client contract does not exist for chainId: ", chainId);
    return null;
  }
};

/**
 * Note: users with empty string role("") are investors
 * @param {number} chainId
 * @param {string} address
 * @param {JsonRpcSigner} signer
 * @returns {Promise<string> | undefined} user/address role
 */
export const getUserRole = async (chainId, address, signer) => {
  const clientContract = getClientContract(chainId, signer);
  if (clientContract) {
    return await clientContract.getRole(address).then((res) => {
      return ethers.utils.parseBytes32String(res?.response?.result[0]);
    });
  }
};

export const getUserRoleWeb3 = async (chainId, address, web3) => {
  const clientContract = getClientContractWeb3(chainId, web3);

  if (clientContract) {
    return await clientContract.methods
      .getRole(address)
      .call({ from: address })
      .then((res) => {
        return ethers.utils.parseBytes32String(res[0]);
      })
      .catch((err) => {
        console.log("errrr: ", err);
      });
  }
};

/**
 * gets user KYC details
 * @param {number} chainId
 * @param {string} address
 * @param {JsonRpcSigner} signer
 * @returns
 */
export const getUserKYCDetails = async (chainId, address, signer) => {
  const clientContract = getClientContract(chainId, signer);
  if (clientContract) {
    const clientKycResult = await clientContract
      .getClientKYC(address)
      .then((res) => {
        return res?.response?.result;
      });
    const fullClientKycResult = await clientContract
      .getFullClientKYC(address)
      .then((res) => {
        return res?.response?.result;
      });

    const userGTNCustomerId = await clientContract
      .getCustodyAccount(address)
      .then((res) => {
        if (res?.response?.result?.length > 0) {
          try {
            const userGtnAccount = res?.response?.result?.find(
              (bnk) =>
                ethers.utils
                  .parseBytes32String(bnk?.currency)
                  ?.toLowerCase() === "GTN:"?.toLowerCase(),
            );
            if (userGtnAccount) {
              const userId = ethers.utils.parseBytes32String(
                userGtnAccount?.account,
              );
              return userId;
            }
          } catch (err) {
            return "";
          }
        }
        return "";
      });

    return {
      userFullName: ethers.utils.parseBytes32String(clientKycResult[0]),
      userFirstName: ethers.utils
        .parseBytes32String(clientKycResult[0])
        .split(" ")[0],
      userKycStatus: Number(clientKycResult[3]),
      userCountry: ethers.utils.parseBytes32String(clientKycResult[2]),
      userEmail: ethers.utils.parseBytes32String(clientKycResult[4]),
      userId: ethers.utils.parseBytes32String(clientKycResult[1]),
      userType: String(fullClientKycResult[1]),
      userGTNCustomerId,
    };
  }
};

export const getUserKYCDetailsWeb3 = async (chainId, address, web3) => {
  const clientContract = getClientContractWeb3(chainId, web3);
  if (clientContract) {
    const clientKycResult = await clientContract.methods
      .getClientKYC(address)
      .call({ from: address })
      .then((res) => {
        return res;
      });
    const fullClientKycResult = await clientContract.methods
      .getFullClientKYC(address)
      .call({ from: address })
      .then((res) => {
        return res;
      });

    const userGTNCustomerId = await clientContract.methods
      .getCustodyAccount(address)
      .call({ from: address })
      .then((res) => {
        if (res?.length > 0) {
          try {
            const userGtnAccount = res?.find(
              (bnk) =>
                ethers.utils
                  .parseBytes32String(bnk?.currency)
                  ?.toLowerCase() === "GTN:"?.toLowerCase(),
            );
            if (userGtnAccount) {
              const userId = ethers.utils.parseBytes32String(
                userGtnAccount?.account,
              );
              return userId;
            }
          } catch (err) {
            return "";
          }
        }
        return "";
      });

    return {
      userFullName: ethers.utils.parseBytes32String(clientKycResult[0]),
      userFirstName: ethers.utils
        .parseBytes32String(clientKycResult[0])
        .split(" ")[0],
      userKycStatus: Number(clientKycResult[3]),
      userCountry: ethers.utils.parseBytes32String(clientKycResult[2]),
      userEmail: ethers.utils.parseBytes32String(clientKycResult[4]),
      userId: ethers.utils.parseBytes32String(clientKycResult[1]),
      userType: String(fullClientKycResult[1]),
      userGTNCustomerId,
    };
  }
};

/**
 * gets user custody accounts
 * @param {number} chainId
 * @param {string} address
 * @param {JsonRpcSigner} signer
 * @returns
 */
export const getUserCustodyBanks = async (chainId, address, signer) => {
  const clientContract = getClientContract(chainId, signer);
  if (clientContract) {
    const userCustodyAccounts = await clientContract
      .getCustodyAccount(address)
      .then((res) => {
        return res?.response?.result;
      });

    return userCustodyAccounts;
  }
};

/**
 * sets user custody account by currency
 * @param {number} chainId
 * @param {string} address
 * @param {JsonRpcSigner} signer
 * @param {string} currency
 * @param {string} accountId
 * @returns
 */
export const setUserCustodyBanks = async (
  chainId,
  address,
  signer,
  currency,
  accountId,
  paymentToken,
  provider,
  web3,
  custodyWalletDetails,
) => {
  if (
    custodyWalletDetails &&
    Object.keys(custodyWalletDetails)?.length > 0 &&
    custodyWalletDetails?.custodyId?.length > 0 &&
    custodyWalletDetails?.address &&
    custodyWalletDetails?.chainId > 0 &&
    web3 &&
    provider
  ) {
    const clientContractWeb3 = getClientContractWeb3(chainId, web3);
    if (clientContractWeb3) {
      const txData = clientContractWeb3.methods
        .setCustodyAccount(
          address,
          ethers.utils.formatBytes32String(currency),
          ethers.utils.formatBytes32String(accountId),
        )
        .encodeABI();
      const txNonce = await web3.eth.getTransactionCount(
        custodyWalletDetails?.address,
      );
      return await handleCustodyTransaction(
        custodyWalletDetails,
        contractAddress[chainId].Client,
        provider,
        "0",
        txData,
        web3.utils.toHex(txNonce),
        "Verified-Network-setCustodyAccount",
      ).then((cusRes) => {
        if (cusRes?.toLowerCase() === "completed") {
          return "completed";
        } else {
          return null;
        }
      });
    } else {
      return null;
    }
  } else {
    const clientContract = getClientContract(chainId, signer);
    if (clientContract) {
      return await clientContract
        .setCustodyAccount(
          address,
          ethers.utils.formatBytes32String(currency),
          ethers.utils.formatBytes32String(accountId),
          paymentToken?.address
            ? {
                paymentToken: paymentToken?.address,
                apiKey: process.env.REACT_APP_BICONOMY_MEE_KEY,
                rpcUrl: ChainConfig[chainId]?.rpcUrls[0],
              }
            : {},
        )
        .then((res) => {
          if (res?.status === 0) {
            return `${ChainConfig[chainId]?.blockExplorerUrls[0]}tx/${res?.response?.hash}`;
          } else {
            return null;
          }
        })
        .catch((err) => {
          console.log("setCustodyAccount error: ", err);
          return null;
        });
    } else {
      return null;
    }
  }
};

/**
 * gets managers for the role passed in a particular country
 * @param {number} chainId
 * @param {JsonRpcSigner} signer
 * @param {string} country
 * @param {string} role
 * @returns {Promise<any []>}
 */
export const getRoleManagersDetails = async (
  chainId,
  signer,
  country,
  role,
) => {
  const clientContract = getClientContract(chainId, signer);
  if (clientContract) {
    const roleManagers = await clientContract
      .getManagers(country, role)
      .then((res) => {
        if (res?.response?.result) {
          return res?.response?.result.map((add) => add?.toLowerCase());
        }
      });
    if (roleManagers) {
      const managersDetails = await fetchUsersByAddresses(
        chainId,
        roleManagers,
      ).then((res) => res);
      if (managersDetails.length) {
        return managersDetails.map((manager) => {
          return {
            address: manager.id,
            name: ethers.utils.parseBytes32String(manager.name),
          };
        });
      }
    }
    return [];
  }
  return [];
};
